import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useState, useEffect } from "react";
import { productService } from "../services/productService";
import {
  UserIcon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  ArrowLeftStartOnRectangleIcon,
  BanknotesIcon,
  Cog6ToothIcon,
  ShoppingBagIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";

const GuestLayout = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const { getItemCount } = useCart();
  const cartItemCount = getItemCount();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await productService.getCategories();
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const navLinks = [
    { path: "/", label: "HOME" },
    { path: "/products", label: "SHOP" },
    { path: "/about", label: "ABOUT" },
    { path: "/rkicha-home", label: "RKICHA HOME" },
    { path: "/contact", label: "CONTACT" },
  ];

  return (
    <div className="min-h-screen bg-info font-sans flex flex-col">
      {/* Header */}
      <header className="bg-info sticky top-0 z-50 border-b border-primary/20">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Left: MENU, SEARCH */}
            <div className="flex items-center space-x-4 md:space-x-6 flex-1">
              <button
                className="flex items-center space-x-2 group"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <span className="text-sm md:text-xs font-bold tracking-[0.2em] text-secondary uppercase group-hover:text-secondary-dark transition-colors">
                  MENU
                </span>
              </button>
              <button
                className="hidden md:flex items-center space-x-2 group"
                onClick={() => navigate("/products")}
              >
                <span className="text-sm md:text-xs font-bold tracking-[0.2em] text-secondary uppercase group-hover:text-secondary-dark transition-colors">
                  Shop
                </span>
              </button>
              <button
                className="hidden md:flex items-center space-x-2 group"
                onClick={() => navigate("/about")}
              >
                <span className="text-sm md:text-xs font-bold tracking-[0.2em] text-secondary uppercase group-hover:text-secondary-dark transition-colors">
                  About
                </span>
              </button>
              <button
                className="hidden md:flex items-center space-x-2 group"
                onClick={() => navigate("/rkicha-home")}
              >
                <span className="text-sm md:text-xs font-bold tracking-[0.2em] text-secondary uppercase group-hover:text-secondary-dark transition-colors">
                  RKICHA HOME
                </span>
              </button>
              <button
              onClick={() => navigate("/contact")}
              className="hidden md:flex items-center space-x-2 group"
            >
              <span className="text-sm md:text-xs font-bold tracking-[0.2em] text-secondary uppercase group-hover:text-secondary-dark transition-colors">
                Contact
              </span>
            </button>
          </div>

          {/* Center: Logo */}
          <div className="flex justify-center flex-1">
            <button
              onClick={() => navigate("/")}
              className="flex items-center"
              aria-label="RKICHA Home"
            >
              <img src="/images/logo.png" alt="RKICHA Logo" className="w-32 md:w-40" />
            </button>
          </div>

          {/* Right: ACCOUNT, CART */}
          <div className="flex items-center justify-end space-x-2 md:space-x-6 flex-1">
            <div className="block text-sm md:text-xs font-bold tracking-[0.2em] text-secondary uppercase hover:text-secondary-dark transition-colors">
              {isAuthenticated ? (
                <div className="relative group">
                  <button
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    onBlur={() => setTimeout(() => setProfileMenuOpen(false), 200)}
                    className="md:w-10 md:h-10 w-8 h-8 rounded-xl bg-secondary flex items-center justify-center shadow-lg border-2 border-secondary/20 hover:scale-105 transition-all"
                    aria-expanded={profileMenuOpen}
                    aria-haspopup="true"
                  >
                    <span className="text-white text-xs font-black">
                      {user?.firstname?.charAt(0)?.toUpperCase()}
                    </span>
                  </button>
                  <div
                    className={`absolute right-0 top-full mt-3 bg-white rounded-sm shadow-xl border border-gray-100 transition-all duration-200 py-4 z-50 ${
                      profileMenuOpen
                        ? "opacity-100 visible translate-y-0"
                        : "opacity-0 invisible -translate-y-2"
                    } lg:group-hover:opacity-100 lg:group-hover:visible lg:group-hover:translate-y-0`}
                  >
                    <div className="text-left px-4 pb-3 mb-3 border-b border-gray-100">
                      <p className="text-xs font-bold text-secondary truncate">
                        {user?.firstname} {user?.lastname}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate("/profile?tab=profile")}
                      className="flex items-center space-x-3 px-4 py-2 text-[12px] md:text-xs text-secondary hover:bg-info transition-colors w-full text-left"
                    >
                      <UserIcon className="w-4 h-4" />
                      <span>My Profile</span>
                    </button>
                    {user?.role === "customer" && (
                      <button
                        onClick={() => navigate("/profile?tab=order")}
                        className="flex items-center space-x-3 px-4 py-2 text-[12px] md:text-xs text-secondary hover:bg-info transition-colors w-full text-left"
                      >
                        <ClipboardDocumentListIcon className="w-4 h-4" />
                        <span>Orders</span>
                      </button>
                    )}
                    {user?.role === "admin" && (
                      <button
                        onClick={() => navigate("/admin")}
                        className="flex items-center space-x-3 px-4 py-2 text-[12px] md:text-xs text-secondary hover:bg-info transition-colors w-full text-left"
                      >
                        <Cog6ToothIcon className="w-4 h-4" />
                        <span>Admin Dashboard</span>
                      </button>
                    )}
                    <button
                      onClick={logout}
                      className="flex items-center space-x-3 px-4 py-2 text-[12px] md:text-xs text-danger hover:bg-red-50 transition-colors w-full text-left mt-2"
                    >
                      <ArrowLeftStartOnRectangleIcon className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => navigate("/login")}
                    className="hidden md:block text-sm md:text-xs font-bold tracking-[0.2em] text-secondary uppercase hover:text-secondary-dark transition-colors flex items-center"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate("/login")}
                    className="md:hidden text-sm md:text-xs font-bold tracking-[0.2em] text-secondary uppercase hover:text-secondary-dark transition-colors flex items-center"
                    aria-label="Login"
                  >
                    <UserCircleIcon className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>
            <button
              onClick={() => navigate("/cart")}
              className="text-sm md:text-xs font-bold tracking-[0.2em] text-secondary uppercase hover:text-secondary-dark transition-colors flex items-center"
              aria-label={`Cart with ${cartItemCount || 0} items`}
            >
              <ShoppingBagIcon className="w-6 h-6" />
              <span className="ml-1">{cartItemCount || 0}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mega Menu / Side Navigation */}
      {isMenuOpen && (
        <div className="fixed inset-0 top-0 z-[60] bg-info flex flex-col items-center justify-start pt-24 w-full md:max-w-[320px] shadow-2xl animate-in slide-in-from-left duration-300">
          <button
            className="absolute top-8 right-8 p-2 text-secondary hover:text-secondary-dark transition-colors"
            onClick={() => setIsMenuOpen(false)}
            aria-label="Close menu"
          >
            <XMarkIcon className="w-8 h-8" />
          </button>
          <nav className="flex flex-col space-y-8 text-center w-full px-8">
            {navLinks.map(link => (
              <button
                key={link.path}
                onClick={() => {
                  navigate(link.path);
                  setIsMenuOpen(false);
                }}
                className={`text-2xl font-bold tracking-[0.2em] uppercase transition-all ${
                  location.pathname === link.path
                    ? "text-secondary scale-105"
                    : "text-secondary/60 hover:text-secondary"
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>
        </div>
      )}
      {/* Overlay for menu */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-dark/20 backdrop-blur-sm z-[55] animate-in fade-in duration-300"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </header>

    {/* Main Content */}
    <main className="flex-1">
      <Outlet />
    </main>

    {/* Footer */}
    <footer className="bg-primary border-t border-primary/20 py-12 md:py-20">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start gap-12 md:gap-8">
          <div className="max-w-xs m-auto md:m-0">
            <img src="/images/logo.png" alt="RKICHA Logo" className="w-32 md:w-64 b-6 opacity-80" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-16 gap-y-8">
            <div className="flex flex-col space-y-4">
              <h4 className="text-xs font-black tracking-widest text-dark uppercase">Shop</h4>
              {navLinks.slice(1, 2).map(link => (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  className="text-xs font-bold tracking-widest text-dark/60 hover:text-dark text-left transition-colors uppercase"
                >
                  {link.label}
                </button>
              ))}
            </div>
            <div className="flex flex-col space-y-4">
              <h4 className="text-xs font-black tracking-widest text-dark uppercase">Experience</h4>
              {navLinks.slice(2).map(link => (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  className="text-xs font-bold tracking-widest text-dark/60 hover:text-dark text-left transition-colors uppercase"
                >
                  {link.label}
                </button>
              ))}
            </div>
            <div className="flex flex-col space-y-4">
              <h4 className="text-xs font-black tracking-widest text-dark uppercase">Legal</h4>
              <button
                onClick={() => navigate("/")}
                className="text-xs font-bold tracking-widest text-dark/60 hover:text-dark text-left transition-colors uppercase"
              >
                Privacy
              </button>
              <button
                onClick={() => navigate("/")}
                className="text-xs font-bold tracking-widest text-dark/60 hover:text-dark text-left transition-colors uppercase"
              >
                Terms
              </button>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-dark/10 flex flex-col md:flex-row justify-between items-center gap-4 text-[10px] font-black tracking-[0.2em] text-dark/40 uppercase">
          <div>&copy; 2026 rkicha. all rights reserved.</div>
          <div className="flex space-x-6">
            <span>Bangkok, Thailand</span>
          </div>
        </div>
      </div>
    </footer>
    </div>
  );
};

export default GuestLayout;
